import { useState, useCallback } from 'react';
import { AxiosError } from 'axios';

interface ErrorState {
  error: string | null;
  isError: boolean;
}

interface UseErrorHandlerReturn extends ErrorState {
  setError: (error: string | null) => void;
  handleError: (error: unknown) => void;
  clearError: () => void;
}

export function useErrorHandler(): UseErrorHandlerReturn {
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback((error: unknown) => {
    console.error('Error caught by useErrorHandler:', error);

    if (error instanceof AxiosError) {
      // Handle Axios errors
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 400:
            setError(extractErrorMessage(error));
            break;
          case 401:
            setError('You are not authorized to perform this action. Please log in and try again.');
            break;
          case 403:
            setError('You do not have permission to perform this action.');
            break;
          case 404:
            setError('The requested resource was not found.');
            break;
          case 409:
            setError(data?.detail || data?.message || 'This action conflicts with existing data. Please check and try again.');
            break;
          case 422:
            setError(data?.detail || data?.message || 'Please check your input and try again.');
            break;
          case 429:
            setError('Too many requests. Please wait a moment and try again.');
            break;
          case 500:
            setError('Server error. Please try again later or contact support.');
            break;
          case 502:
          case 503:
          case 504:
            setError('Service temporarily unavailable. Please try again later.');
            break;
          default:
            setError(data?.detail || data?.message || `Request failed with status ${status}.`);
        }
      } else if (error.request) {
        // Network error
        setError('Network error. Please check your connection and try again.');
      } else {
        // Other error
        setError('An unexpected error occurred. Please try again.');
      }
    } else if (error instanceof Error) {
      // Handle generic errors
      setError(error.message || 'An unexpected error occurred. Please try again.');
    } else if (typeof error === 'string') {
      // Handle string errors
      setError(error);
    } else {
      // Handle unknown errors
      setError('An unexpected error occurred. Please try again.');
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    error,
    isError: !!error,
    setError,
    handleError,
    clearError,
  };
}

// Utility function to extract error message from various error types
export function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    
    // Handle field-level validation errors
    if (data && typeof data === 'object') {
      const fieldErrors: string[] = [];
      const reservedKeys = new Set(['detail', 'message', 'error', 'non_field_errors']);

      for (const [field, value] of Object.entries(data)) {
        if (reservedKeys.has(field) || value == null) continue;
        const text = Array.isArray(value)
          ? value.filter((item) => typeof item === 'string').join(', ')
          : typeof value === 'string'
            ? value
            : '';
        if (!text) continue;
        const label = field.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
        fieldErrors.push(`${label}: ${text}`);
      }

      if (data.non_field_errors) {
        const nonField = Array.isArray(data.non_field_errors)
          ? data.non_field_errors.join(', ')
          : data.non_field_errors;
        if (typeof nonField === 'string' && nonField) {
          fieldErrors.push(nonField);
        }
      }
      
      // If we have field errors, return them
      if (fieldErrors.length > 0) {
        return fieldErrors.join('; ');
      }
      
      // Check for non-field errors
      if (data.detail) {
        return Array.isArray(data.detail) ? data.detail.join(', ') : data.detail;
      }
      if (data.message) {
        return Array.isArray(data.message) ? data.message.join(', ') : data.message;
      }
      if (data.error) {
        return Array.isArray(data.error) ? data.error.join(', ') : data.error;
      }
    }
    
    if (error.message) {
      return error.message;
    }
    return 'Request failed';
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  return 'An unexpected error occurred';
}

// Utility function to check if error is a validation error
export function isValidationError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    return error.response?.status === 400 || error.response?.status === 422;
  }
  return false;
}

// Utility function to check if error is a network error
export function isNetworkError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    return !error.response && error.request;
  }
  return false;
}

// Utility function to check if error is an authentication error
export function isAuthError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    return error.response?.status === 401 || error.response?.status === 403;
  }
  return false;
}


